"use client";

import { c as _c } from "react/compiler-runtime";
import { cva } from "class-variance-authority";
import { buttonVariants } from "fumadocs-ui/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "fumadocs-ui/components/ui/popover";
import { useCopyButton } from "fumadocs-ui/utils/use-copy-button";
import { Check, ChevronDown, Copy, ExternalLinkIcon } from "lucide-react";
import { useMemo, useState } from "react";

// import { Logo } from "@/components/logo";
import { cn } from "@/lib/cn";
import { Logo } from "../logo";
const cache = new Map<string, string>();
export function LLMCopyButton({
  /**
   * A URL to fetch the raw Markdown/MDX content of page
   */
  markdownUrl
}: {
  markdownUrl: string;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [checked, onClick] = useCopyButton(async () => {
    const cached = cache.get(markdownUrl);
    if (cached) {
      await navigator.clipboard.writeText(cached);
      return;
    }
    setIsLoading(true);
    try {
      await navigator.clipboard.write([new ClipboardItem({
        "text/plain": fetch(markdownUrl).then(async res => {
          const content = await res.text();
          cache.set(markdownUrl, content);
          return content;
        })
      })]);
    } finally {
      setIsLoading(false);
    }
  });
  return <button disabled={isLoading} className={cn(buttonVariants({
    color: "secondary",
    size: "sm",
    className: "gap-2 [&_svg]:size-3.5 [&_svg]:text-fd-muted-foreground"
  }))} onClick={onClick}>
			{checked ? <Check /> : <Copy />}
			Copy Markdown
		</button>;
}
const optionVariants = cva("text-sm p-2 rounded-lg inline-flex items-center gap-2 hover:text-fd-accent-foreground hover:bg-fd-accent [&_svg]:size-4");
export function ViewOptions(t0) {
  const $ = _c(29);
  const {
    markdownUrl,
    githubUrl
  } = t0;
  let t1;
  if ($[0] !== markdownUrl) {
    t1 = typeof window !== "undefined" ? new URL(markdownUrl, window.location.origin) : "loading";
    $[0] = markdownUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fullMarkdownUrl = t1;
  const q = `Read ${fullMarkdownUrl}, I want to ask questions about it.`;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <svg fill="currentColor" role="img" viewBox="0 0 24 24"><title>GitHub</title><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" /></svg>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== githubUrl) {
    t3 = {
      title: "Open in GitHub",
      href: githubUrl,
      icon: t2
    };
    $[3] = githubUrl;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== q) {
    t4 = new URLSearchParams({
      hints: "search",
      q
    });
    $[5] = q;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = `https://chatgpt.com/?${t4}`;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <svg role="img" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><title>OpenAI</title><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" /></svg>;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5) {
    t7 = {
      title: "Open in ChatGPT",
      href: t5,
      icon: t6
    };
    $[8] = t5;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== q) {
    t8 = new URLSearchParams({
      q
    });
    $[10] = q;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = `https://claude.ai/new?${t8}`;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <svg fill="currentColor" role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><title>Anthropic</title><path d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" /></svg>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t9) {
    t11 = {
      title: "Open in Claude",
      href: t9,
      icon: t10
    };
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== q) {
    t12 = process.env.NODE_ENV === "development" ? `http://localhost:3003?${new URLSearchParams({
      q
    })}&hints=search&model=google-ai-studio/gemini-3-flash-preview` : `https://llm-playground.agenzo.com?${new URLSearchParams({
      q
    })}&hints=search&model=google-ai-studio/gemini-3-flash-preview`;
    $[15] = q;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Logo />;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t12) {
    t14 = {
      title: "Open in LLM Gateway",
      href: t12,
      icon: t13
    };
    $[18] = t12;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t11 || $[21] !== t14 || $[22] !== t3 || $[23] !== t7) {
    t15 = [t3, t7, t11, t14];
    $[20] = t11;
    $[21] = t14;
    $[22] = t3;
    $[23] = t7;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const items = t15;
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = cn(buttonVariants({
      color: "secondary",
      size: "sm",
      className: "gap-2"
    }));
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <PopoverTrigger className={t16}>Open<ChevronDown className="size-3.5 text-fd-muted-foreground" /></PopoverTrigger>;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== items) {
    t18 = <Popover>{t17}<PopoverContent className="flex flex-col overflow-auto">{items.map(_temp)}</PopoverContent></Popover>;
    $[27] = items;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  return t18;
}
function _temp(item) {
  return <a key={item.href} href={item.href} rel="noreferrer noopener" target="_blank" className={cn(optionVariants())}>{item.icon}{item.title}<ExternalLinkIcon className="text-fd-muted-foreground size-3.5 ms-auto" /></a>;
}